import { type Chapter } from '~/hooks/useAssetVersionChaptersList';

export type SeekbarChapter = Chapter & {
  type: 'chapter' | 'gap';
};

const makeChapter = (chapter: Chapter): SeekbarChapter => ({
  ...chapter,
  type: 'chapter',
});

const makeGap = (startTime: number, endTime: number): SeekbarChapter => ({
  type: 'gap',
  title: '',
  summary: '',
  startTime,
  endTime,
});

/**
 * Fills uncovered time ranges in the chapter list with synthetic gap entries so
 * that the resulting array spans the full [0, duration] range.
 *
 * Plain API chapters are tagged here so the gap discriminant stays local to
 * seekbar rendering instead of leaking into chapter panel consumers.
 *
 * Chapters that start at or beyond the video duration are dropped entirely.
 * Chapters whose end time exceeds the video duration are clamped to duration.
 */
export const normalizeChaptersWithGaps = (chapters: Chapter[], duration: number): SeekbarChapter[] => {
  const typedChapters = chapters.map(makeChapter);

  if (duration <= 0 || chapters.length === 0) {
    return typedChapters;
  }

  const clamped = typedChapters
    .filter((c) => c.startTime < duration)
    .map((c) => (c.endTime > duration ? { ...c, endTime: duration } : c));

  if (clamped.length === 0) {
    return [];
  }

  const sorted = [...clamped].sort((a, b) => a.startTime - b.startTime);
  const result: SeekbarChapter[] = [];

  const leadGap = sorted[0].startTime;

  if (leadGap > 0) {
    result.push(makeGap(0, sorted[0].startTime));
  }

  for (let i = 0; i < sorted.length; i++) {
    result.push(sorted[i]);

    if (i < sorted.length - 1) {
      const gapStart = sorted[i].endTime;
      const gapEnd = sorted[i + 1].startTime;

      if (gapEnd > gapStart) {
        result.push(makeGap(gapStart, gapEnd));
      }
    }
  }

  const tailGap = duration - sorted[sorted.length - 1].endTime;

  if (tailGap > 0) {
    result.push(makeGap(sorted[sorted.length - 1].endTime, duration));
  }

  return result;
};
